import { cloneDeep } from 'lodash'
import type {
  IEntityDataItem,
  TEntityMaskItem,
  IEntityParentObjectForCreate,
} from '~/types/models'
import { isEntityMaskData, assertIsDefined } from '~/types/models'
import { getEntityInputFields } from '~/services/entityInputFieldsService/getEntityInputFields'
import {
  splitWrapperPath,
  getValueAtWrapperPath,
} from '~/cynqueModules/wrapperObjectPath'
import {
  isDelimitedListField,
  splitDelimitedString,
} from '~/cynqueModules/componentHelpers/entityMaskForm/delimitedList'

export async function callGetEntityInputFieldsService({
  mn,
  inputFieldsOverride,
  inputFieldsEntityNameOverride,
  entityName,
  isSubentityDialog,
}: {
  mn: string
  inputFieldsOverride: TEntityMaskItem[] | undefined
  inputFieldsEntityNameOverride: string | undefined
  entityName: string
  isSubentityDialog: boolean
}) {
  if (inputFieldsOverride && !isSubentityDialog) {
    return cloneDeep(inputFieldsOverride) // To avoid Vuex aliasing
  } else {
    const { inputFields } = await getEntityInputFields({
      callingMn: `${mn} (_getInputFields) `,
      entityName: inputFieldsEntityNameOverride || entityName,
    })
    return inputFields
  }
}

export async function setInputFieldsSelectLists({
  mn,
  inputFields,
  entityParentObjectForCreate,
}: {
  mn: string
  inputFields: TEntityMaskItem[]
  entityParentObjectForCreate: IEntityParentObjectForCreate | undefined
}) {
  const fn = `${mn} (setInputFieldsSelectLists) `
  if (!inputFields) {
    throw new Error(`${fn} inputFields is falsey`)
  }
  console.debug(
    `${fn} We need to initialize all function-driven Select Lists\n` +
      `${fn} or any pre-population of select fields won't display...`
  )

  const onlyEntityMaskDatasWithSetSelectItemsFunctions = inputFields
    .filter(isEntityMaskData)
    .filter((inputField) => !!inputField.setSelectItems)

  await Promise.all(
    onlyEntityMaskDatasWithSetSelectItemsFunctions.map(
      async (entityMaskData) => {
        console.debug(`${fn} setting for inputField:`, entityMaskData)
        try {
          assertIsDefined(entityMaskData.setSelectItems)
          await entityMaskData.setSelectItems(
            inputFields,
            entityParentObjectForCreate,
            entityMaskData
          )
          console.debug(`${fn} done setting for inputField`, entityMaskData)
        } catch (e) {
          console.debug(
            `${fn} CAUGHT ERROR IN GET-INPUT-FIELDS, SET-SELECT-ITEMS`
          )
          throw e
        }
      }
    )
  )
  console.debug(`${fn} inputFields array with items select lists set...`)
  console.dir(inputFields)
}

//   onlyEntityMaskDatas
//     .filter((inputField) => !!inputField.setSelectItems)
//     .forEach(async (entityMaskData, index, array) => {
//       console.debug(`${fn} setting for inputField:`, entityMaskData)
//       try {
//         assertIsDefined(entityMaskData.setSelectItems)
//         entityMaskData.items = await entityMaskData.setSelectItems(
//           array,
//           entityParentObjectForCreate
//         )
//         console.debug(`${fn} set for inputField`, entityMaskData)
//       } catch (e) {
//         console.debug(
//           `${fn} CAUGHT ERROR IN GET-INPUT-FIELDS, SET-SELECT-ITEMS`
//         )
//         throw e
//       }
//     })
//   console.debug(`${fn} inputFields array with items select lists set...`)
//   console.dir(inputFields)
// }

export function setInputFieldsStartAndValue({
  mn,
  entityData,
  inputFields,
}: {
  mn: string
  entityData: IEntityDataItem
  inputFields: TEntityMaskItem[]
}) {
  const fn = `${mn}(setInputFieldsStartAndValue) `
  if (!entityData) {
    throw new Error(`${fn} entityData is falsey`)
  }
  if (!inputFields) {
    throw new Error(`${fn} inputFields is falsey`)
  }
  console.debug(
    `${fn} We want to edit existing data, not enter new data, so we're setting\n` +
      `${fn} each inputField's 'start' and 'value' values from passed entityData...`
  )
  const onlyEntityMaskDatas = inputFields.filter(isEntityMaskData)

  onlyEntityMaskDatas.forEach((inputField, index) => {
    console.debug(`${fn} inputField ${index}:`, inputField)
    // The wrapperObjectName may be a dot-path of arbitrary depth (e.g.
    // 'supplierProduct.product'); resolve the value at wrapperObjectName + name,
    // or just name when there is no wrapper.
    const segments = inputField.wrapperObjectName
      ? [...splitWrapperPath(inputField.wrapperObjectName), inputField.name]
      : [inputField.name]
    const valueToSet = getValueAtWrapperPath(entityData, segments)

    // A list edited over a stored string: split it here, before anything
    // compares start with value, into two SEPARATE arrays (the widget replaces
    // `value`; `start` must stay the untouched original).
    if (isDelimitedListField(inputField)) {
      inputField.start = splitDelimitedString(valueToSet)
      inputField.value = splitDelimitedString(valueToSet)
      return
    }

    if (
      valueToSet ||
      (typeof valueToSet === 'boolean' && valueToSet !== null) ||
      (typeof valueToSet === 'number' && valueToSet === 0)
    ) {
      console.debug(`${fn} Setting value and start to`, valueToSet)
      inputField.value = inputField.start =
        valueToSet as typeof inputField.value
    }
  })
  console.debug(
    `${fn} inputFields array with 'start' and 'value' values set...`
  )
  console.dir(inputFields)
}
